import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import type { PaymentGridColumn } from 'domain/payments/types';

export interface BeneficiaryItem {
  id: string;
  nickname: string;
  isDefault: boolean;
  [field: string]: unknown;
}

const TITLE_FIELD = 'nickname';
const DEFAULT_FIELD = 'isDefault';

const OverflowCell: React.FC<{ value: string }> = ({ value }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  const handleMouseEnter = useCallback(() => {
    const el = ref.current;
    if (el) setTruncated(el.scrollWidth > el.clientWidth);
  }, []);
  return (
    <Tooltip title={truncated ? value : ''} placement="top">
      <Typography ref={ref} variant="body2" sx={{ fontSize: 13 }} noWrap onMouseEnter={handleMouseEnter}>
        {value}
      </Typography>
    </Tooltip>
  );
};

interface Props {
  beneficiary: BeneficiaryItem;
  headers: PaymentGridColumn[];
  onEdit: () => void;
  onDelete: () => void;
  onSetDefault: () => void;
}

const BeneficiaryCard: React.FC<Props> = ({ beneficiary, headers, onEdit, onDelete, onSetDefault }) => {
  const detailHeaders = headers.filter((h) => h.field !== TITLE_FIELD && h.field !== DEFAULT_FIELD);
  const { formatMessage } = useIntl();

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 2,
        mb: 1.5,
        borderLeft: beneficiary.isDefault ? '3px solid' : '1px solid',
        borderLeftColor: beneficiary.isDefault ? alpha(theme.palette.secondary.main, 0.5) : theme.palette.divider,
        borderRadius: 1,
      })}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Box sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }}>
          <AccountBalanceIcon fontSize="medium" />
        </Box>

        <Box flex={1} minWidth={0}>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.75}>
            <Typography variant="body2" fontWeight={600}>
              {beneficiary.nickname}
            </Typography>
            {beneficiary.isDefault && (
              <Chip
                label={formatMessage({ id: 'beneficiary.default', defaultMessage: 'Default' })}
                size="small"
                color="secondary"
                sx={{ fontWeight: 600, fontSize: 11, color: 'common.white' }}
              />
            )}
          </Stack>

          {detailHeaders.length > 0 && (
            <Box
              sx={{ display: 'grid', gridTemplateColumns: `repeat(${detailHeaders.length}, minmax(0, 1fr))`, gap: 1.5 }}
            >
              {detailHeaders.map(({ field, headerName }) => {
                const value = beneficiary[field];
                return (
                  <Box key={field}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}
                    >
                      {headerName}
                    </Typography>
                    <OverflowCell value={value == null || value === '' ? '—' : String(value)} />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Stack direction="row" spacing={0.5} flexShrink={0}>
          <IconButton
            size="small"
            onClick={beneficiary.isDefault ? undefined : onSetDefault}
            title={formatMessage({ id: 'beneficiary.action.setDefault', defaultMessage: 'Set as default' })}
            sx={{ cursor: beneficiary.isDefault ? 'default' : 'pointer' }}
          >
            {beneficiary.isDefault ? (
              <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
            ) : (
              <StarBorderIcon fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={onEdit}
            title={formatMessage({ id: 'button.edit', defaultMessage: 'Edit' })}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onDelete}
            color="error"
            title={formatMessage({ id: 'button.delete', defaultMessage: 'Delete' })}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default BeneficiaryCard;
